// Ch78 · ONE loader for "everything we know about this person".
//
// WHY THIS EXISTS
// DetailDrawer (the Clients-page drawer) loads eight datasets with inline
// functions nobody else can call. Grant asked for a complete client/lead
// MODAL from Messages — "present ALL data we have that could be useful" —
// which means the same eight reads plus three the drawer never did: the
// client row itself (every column), the ORIGIN LEAD (the website form the
// person actually filled in, via clients.origin_lead_id), and the assigned
// agent's profile. This hook is those reads, lifted verbatim from the drawer
// where they existed and added where they did not.
//
// Contract: every dataset is tri-state — `undefined` = not loaded yet,
// `[]`/`null` = loaded and empty, and a failed read lands in `errors` by
// name. A failure NEVER renders as an empty-but-calm section (the estate's
// silent-noop rule). The origin lead is the one read that may legitimately
// be refused by RLS for a non-elevated role; that shows as
// errors.lead = "not visible", not as "no lead".
//
// Register (FUTURE-IMPROVEMENTS): DetailDrawer should consume this hook so
// the two surfaces cannot drift. Left out of this PR on purpose — the drawer
// is 1,900 lines and under no test that would catch a rewiring slip.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { fetchWithAuth } from "../../lib/api";
import type {
  Agent,
  Client,
  ClientActivity,
  ClientNote,
  ClientTask,
  Lead,
  SavedSearch,
  SentGuestCard,
  SentListing,
} from "./types";
import type { Invoice } from "./invoiceTypes";
import type { IntentProfileView } from "./IntentProfilePanel";
import { formatActivityDescription } from "./helpers";

const SHORTLIST_OUTCOME_UI_ENABLED =
  process.env.REACT_APP_SHORTLIST_OUTCOME_UI_ENABLED === "true";

/** The client row with EVERY column the table carries. `Client` names the
 * ones the app types; the index signature keeps the rest visible so the
 * dialog can show what the type forgot. */
export type ClientRecordRow = Client & {
  origin_lead_id?: string | null;
  updated_at?: string;
  user_id?: string;
  [column: string]: unknown;
};

export interface ClientRecord {
  client: ClientRecordRow | null | undefined;
  agent: Agent | null | undefined;
  /** The website form the person submitted, when this client was converted
   * from a lead. `null` = converted-from-nothing (manual intake) or the lead
   * row is gone; see `errors.lead` for "could not read". */
  lead: Lead | null | undefined;
  activities: ClientActivity[] | undefined;
  notes: ClientNote[] | undefined;
  tasks: ClientTask[] | undefined;
  savedSearches: SavedSearch[] | undefined;
  sentListings: SentListing[] | undefined;
  sentGuestCards: SentGuestCard[] | undefined;
  invoices: Invoice[] | undefined;
  intentProfile: IntentProfileView | null | undefined;
  /** Read failures BY NAME. An entry here means that section must say so. */
  errors: Partial<Record<keyof Omit<ClientRecord, "errors">, string>>;
  loading: boolean;
  reload: () => void;
}

const message = (err: unknown): string =>
  (err as { message?: string } | null)?.message || "read failed";

export function useClientRecord(clientId: string | null): ClientRecord {
  const [state, setState] = useState<Omit<ClientRecord, "reload">>({
    client: undefined,
    agent: undefined,
    lead: undefined,
    activities: undefined,
    notes: undefined,
    tasks: undefined,
    savedSearches: undefined,
    sentListings: undefined,
    sentGuestCards: undefined,
    invoices: undefined,
    intentProfile: undefined,
    errors: {},
    loading: false,
  });
  const [tick, setTick] = useState(0);
  const context = useRef({ clientId, epoch: 0 });
  if (context.current.clientId !== clientId) {
    context.current = { clientId, epoch: context.current.epoch + 1 };
  }
  const epoch = context.current.epoch;
  const reload = useCallback(() => {
    if (context.current.clientId === clientId && context.current.epoch === epoch) {
      setTick(t => t + 1);
    }
  }, [clientId, epoch]);
  // A late response for a PREVIOUS client must never paint over the current
  // one (the persistence lane's client-id-synchronization rule).
  const generation = useRef(0);

  useEffect(() => {
    if (!clientId) {
      setState((s) => ({ ...s, client: null, loading: false }));
      return;
    }
    const gen = ++generation.current;
    const alive = () => generation.current === gen;
    const patch = (p: Partial<Omit<ClientRecord, "reload">>) => {
      if (alive()) setState((s) => ({ ...s, ...p, errors: { ...s.errors, ...(p.errors || {}) } }));
    };

    setState(previous => previous.client?.id === clientId
      ? { ...previous, errors: {}, loading: true }
      : {
      client: undefined,
      agent: undefined,
      lead: undefined,
      activities: undefined,
      notes: undefined,
      tasks: undefined,
      savedSearches: undefined,
      sentListings: undefined,
      sentGuestCards: undefined,
      invoices: undefined,
      intentProfile: undefined,
      errors: {},
      loading: true,
    });

    const loadClient = async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("*")
          .eq("id", clientId)
          .maybeSingle();
        if (error) throw error;
        const row = (data as unknown as ClientRecordRow | null) ?? null;
        patch({ client: row });
        if (!row) return;
        // Agent profile — the rail's read, verbatim.
        if (row.assigned_agent_id) {
          try {
            const { data: profile, error: pErr } = await supabase
              .from("profiles")
              .select("id,full_name,email,avatar_url,role")
              .eq("id", row.assigned_agent_id)
              .maybeSingle();
            if (pErr) throw pErr;
            patch({ agent: (profile as unknown as Agent | null) ?? null });
          } catch (err) {
            patch({ agent: null, errors: { agent: message(err) } });
          }
        } else {
          patch({ agent: null });
        }
        // Origin lead — the ONE read a non-elevated role may not be allowed.
        if (row.origin_lead_id) {
          try {
            const { data: lead, error: lErr } = await supabase
              .from("leads")
              .select("*")
              .eq("id", row.origin_lead_id)
              .maybeSingle();
            if (lErr) throw lErr;
            patch({ lead: (lead as unknown as Lead | null) ?? null });
          } catch (err) {
            patch({ lead: null, errors: { lead: message(err) } });
          }
        } else {
          patch({ lead: null });
        }
      } catch (err) {
        // Keep an already visible same-client workspace mounted on read failure.
        if (alive()) setState(previous => ({ ...previous,
          client: previous.client?.id === clientId ? previous.client : null,
          errors: { ...previous.errors, client: message(err) },
        }));
      }
    };

    // ---- the drawer's eight reads, lifted verbatim (DetailDrawer.tsx) ----
    const loadActivities = async () => {
      try {
        const { data, error } = await supabase
          .from("activities")
          .select("*")
          .eq("entity_id", clientId)
          .order("created_at", { ascending: false })
          .limit(50);
        if (error) throw error;
        const unique = (data || []).reduce((acc: any[], curr: any) => {
          if (!acc.find((a) => a.id === curr.id)) acc.push(curr);
          return acc;
        }, []);
        patch({
          activities: unique.map((a: any) => ({
            id: a.id,
            activity_type: a.activity_type,
            description: formatActivityDescription(a.activity_type, a.metadata),
            metadata: a.metadata,
            created_at: a.created_at,
          })),
        });
      } catch (err) {
        patch({ activities: [], errors: { activities: message(err) } });
      }
    };

    const loadNotes = async () => {
      try {
        const { data, error } = await supabase
          .from("client_notes")
          .select("*")
          .eq("client_id", clientId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        patch({ notes: (data || []) as ClientNote[] });
      } catch (err) {
        patch({ notes: [], errors: { notes: message(err) } });
      }
    };

    const loadTasks = async () => {
      try {
        const taskSelect: string = SHORTLIST_OUTCOME_UI_ENABLED
          ? "*, question_payload:shortlist_question_payloads(question_text,deleted_at)"
          : "*";
        const { data, error } = await supabase
          .from("client_tasks")
          .select(taskSelect as any)
          .eq("client_id", clientId)
          .order("status", { ascending: true })
          .order("due_date", { ascending: true, nullsFirst: false })
          .order("created_at", { ascending: false });
        if (error) throw error;
        const statusOrder: Record<string, number> = { pending: 0, completed: 1, cancelled: 2 };
        const sorted = ((data || []) as unknown as ClientTask[]).sort(
          (a, b) => (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0)
        );
        patch({ tasks: sorted });
      } catch (err) {
        patch({ tasks: [], errors: { tasks: message(err) } });
      }
    };

    const loadSavedSearches = async () => {
      try {
        const { data, error } = await supabase
          .from("saved_searches")
          .select("*")
          .eq("client_id", clientId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        patch({ savedSearches: (data || []) as SavedSearch[] });
      } catch (err) {
        patch({ savedSearches: [], errors: { savedSearches: message(err) } });
      }
    };

    const loadSentListings = async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("sent_list")
          .eq("id", clientId)
          .single();
        if (error) throw error;
        const sentListData = (data as any)?.sent_list || [];
        const mapped: SentListing[] = sentListData.map((item: any, index: number) => ({
          id: `${clientId}-${index}`,
          client_id: clientId,
          recipient_email: item.recipient_email,
          sent_at: item.sent_at,
          tracking_id: item.tracking_id,
          email_html: item.email_html,
          property_count: item.properties?.length || 0,
          unit_count:
            item.properties?.reduce(
              (sum: number, p: any) => sum + (p.units?.length || 0),
              0
            ) || 0,
          properties: item.properties,
          subject: item.subject,
          locator: item.locator,
          personal_message: item.personal_message,
        }));
        mapped.sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
        patch({ sentListings: mapped });
      } catch (err) {
        patch({ sentListings: [], errors: { sentListings: message(err) } });
      }
    };

    const loadSentGuestCards = async () => {
      try {
        const { data, error } = await supabase
          .from("sent_guest_cards")
          .select("*")
          .eq("client_id", clientId)
          .order("sent_at", { ascending: false });
        if (error) throw error;
        patch({ sentGuestCards: (data || []) as SentGuestCard[] });
      } catch (err) {
        patch({ sentGuestCards: [], errors: { sentGuestCards: message(err) } });
      }
    };

    const loadInvoices = async () => {
      try {
        const { data, error } = await supabase
          .from("invoices")
          .select("*")
          .eq("client_id", clientId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        patch({ invoices: (data || []) as Invoice[] });
      } catch (err) {
        patch({ invoices: [], errors: { invoices: message(err) } });
      }
    };

    const loadIntent = async () => {
      try {
        const profile = await fetchWithAuth<IntentProfileView | null>(
          `/intent-profiles/client/${clientId}`
        );
        patch({ intentProfile: profile ?? null });
      } catch (err) {
        patch({ intentProfile: null, errors: { intentProfile: message(err) } });
      }
    };

    Promise.all([
      loadClient(),
      loadActivities(),
      loadNotes(),
      loadTasks(),
      loadSavedSearches(),
      loadSentListings(),
      loadSentGuestCards(),
      loadInvoices(),
      loadIntent(),
    ]).finally(() => patch({ loading: false }));
    return () => { generation.current += 1; };
  }, [clientId, tick]);

  return { ...state, reload };
}
